/**
 * The client card's „Ustawienia rozliczeń" dialog: this client's self-booking exception (R2), read
 * against the trainer's general setting, and the one write that changes it.
 *
 * Mounted only while the dialog is open (the popup unmounts when closed), so the reads start when
 * the trainer asks and a new opening starts from the state the server holds, not the last pick.
 *
 * ── Two reads, one gate ────────────────────────────────────────────────────────────────────────
 * The exception and the general policy: the first names the state, the second what „Jak w
 * ustawieniach ogólnych" means today. The dialog waits for both.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { bookingPolicyQueryOptions } from '~/features/credit-ledger';
import {
  deleteBookingPolicyCustomerCustomerIdSelfBooking,
  putBookingPolicyCustomerCustomerIdSelfBooking,
} from '~/shared/api/generated/endpoints/booking-policy/booking-policy';
import { useSkeletonGate, type SkeletonGate } from '~/shared/lib/useSkeletonGate';

import { clientSelfBookingQueryOptions } from '../api/clientDetailQueries';
import {
  selfBookingChoiceOf,
  selfBookingWrite,
  type SelfBookingChoice,
  type SelfBookingWrite,
} from '../model/selfBooking';

export type SelfBookingContent =
  | { readonly kind: 'waiting' }
  | { readonly kind: 'failed' }
  | {
      readonly kind: 'ready';
      /** The state the server holds. */
      readonly current: SelfBookingChoice;
      /** The general setting, as it reads now. */
      readonly generalOn: boolean;
    };

export interface SelfBookingState {
  readonly gate: SkeletonGate;
  readonly content: SelfBookingContent;
  /** The state the dialog shows — the trainer's pick, or the server's until they pick. */
  readonly chosen: SelfBookingChoice | null;
  readonly choose: (choice: SelfBookingChoice) => void;
  readonly retry: () => void;
  /** Send the pick; `onDone` runs once the server holds it (at once, when nothing changed). */
  readonly save: (onDone: () => void) => void;
  readonly saving: boolean;
  readonly saveFailed: boolean;
}

export function useSelfBooking(clientId: number): SelfBookingState {
  const queryClient = useQueryClient();
  const permissionOptions = clientSelfBookingQueryOptions(clientId);
  const permission = useQuery(permissionOptions);
  const policy = useQuery(bookingPolicyQueryOptions());
  const [picked, setPicked] = useState<SelfBookingChoice | null>(null);

  const gate = useSkeletonGate(
    (permission.isPending && permission.errorUpdateCount === 0) ||
      (policy.isPending && policy.errorUpdateCount === 0),
  );

  const write = useMutation({
    mutationFn: (next: Exclude<SelfBookingWrite, { kind: 'none' }>) =>
      next.kind === 'follow'
        ? deleteBookingPolicyCustomerCustomerIdSelfBooking(clientId)
        : putBookingPolicyCustomerCustomerIdSelfBooking(clientId, { allowed: next.allowed }),
    onSuccess: (answer) => {
      queryClient.setQueryData(permissionOptions.queryKey, answer);
    },
  });

  const content: SelfBookingContent = gate.waiting
    ? { kind: 'waiting' }
    : permission.data === undefined || policy.data === undefined
      ? { kind: 'failed' }
      : {
          kind: 'ready',
          current: selfBookingChoiceOf(permission.data.overrideAllowed ?? null),
          generalOn: policy.data.selfBookingEnabled === true,
        };
  const chosen = content.kind === 'ready' ? (picked ?? content.current) : null;

  return {
    gate,
    content,
    chosen,
    choose: (choice) => {
      write.reset();
      setPicked(choice);
    },
    retry: () => {
      if (permission.isError) void permission.refetch();
      if (policy.isError) void policy.refetch();
    },
    save: (onDone) => {
      if (content.kind !== 'ready' || chosen === null || write.isPending) return;
      const next = selfBookingWrite(content.current, chosen);
      if (next.kind === 'none') {
        onDone();
        return;
      }
      write.mutate(next, { onSuccess: onDone });
    },
    saving: write.isPending,
    saveFailed: write.isError,
  };
}
